/**
 * Electron Renderer Logger
 *
 * Captures console.error, window.onerror, and unhandled promise rejections
 * in the renderer process and sends them to the main process via IPC
 * for persistence in ~/.config/asgard-studio/logs/renderer.log.
 *
 * Call initElectronLogger() once at app startup (before React renders).
 * Safe to call in non-Electron environments (browser/cloud) - becomes a no-op.
 */

let initialized = false;

function sendLog(level: 'error' | 'warn' | 'info', message: string): void {
  try {
    window.electronAPI?.sendLog?.(level, message);
  } catch {
    // Silently ignore - logging should never crash the app
  }
}

function formatError(error: unknown): string {
  if (error instanceof Error) {
    return error.stack || `${error.name}: ${error.message}`;
  }
  if (typeof error === 'string') {
    return error;
  }
  try {
    return JSON.stringify(error);
  } catch {
    return String(error);
  }
}

function formatArgs(args: unknown[]): string {
  return args
    .map((arg) => {
      if (typeof arg === 'string') return arg;
      if (arg instanceof Error) return formatError(arg);
      try {
        return JSON.stringify(arg);
      } catch {
        return String(arg);
      }
    })
    .join(' ');
}

export function initElectronLogger(): void {
  if (initialized) return;
  if (!window.electronAPI?.sendLog) return;

  initialized = true;

  // Intercept console.error
  const originalConsoleError = console.error;
  console.error = (...args: unknown[]) => {
    originalConsoleError.apply(console, args);
    sendLog('error', formatArgs(args));
  };

  // Intercept console.warn
  const originalConsoleWarn = console.warn;
  console.warn = (...args: unknown[]) => {
    originalConsoleWarn.apply(console, args);
    sendLog('warn', formatArgs(args));
  };

  // Capture uncaught errors
  window.addEventListener('error', (event: ErrorEvent) => {
    const message = event.error
      ? formatError(event.error)
      : `${event.message} at ${event.filename}:${event.lineno}:${event.colno}`;
    sendLog('error', `[UncaughtError] ${message}`);
  });

  // Capture unhandled promise rejections
  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    sendLog('error', `[UnhandledRejection] ${formatError(event.reason)}`);
  });
}

/**
 * Manually send an error to the renderer log.
 * Use this from ErrorBoundary or other catch blocks.
 */
export function logErrorToFile(error: Error, context?: string): void {
  const prefix = context ? `[${context}] ` : '';
  sendLog('error', `${prefix}${formatError(error)}`);
}
